<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { RefreshIcon } from "@heroicons/vue/outline";
import InputLabel from "@/components/ui/InputLabel.vue";
import { rotateImageFile } from "@/helpers/utils/image-rotation";

type Props = {
  modelValue?: File | null;
  currentImageUrl?: string;
  label?: string;
  labelDisabled?: boolean;
  square?: boolean;
  large?: boolean;
  showRotateButton?: boolean;
};

type Emits = {
  (e: "update:modelValue", value: File | null): void;
  (e: "change", value: File): void;
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  currentImageUrl: "",
  label: "",
  labelDisabled: false,
  square: false,
  large: false,
  showRotateButton: true,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const fileInputRef = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string>("");
const currentFile = ref<File | null>(null);
const isRotating = ref(false);

function triggerFileInput(): void {
  fileInputRef.value?.click();
}

function handleFileChange(event: Event): void {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  // Store the file
  currentFile.value = file;

  // Create preview URL
  previewUrl.value = URL.createObjectURL(file);

  // Emit events
  emit("update:modelValue", file);
  emit("change", file);
}

async function rotateImage(): Promise<void> {
  if (isRotating.value || !currentFile.value) return;

  try {
    isRotating.value = true;

    const rotatedFile = await rotateImageFile(currentFile.value);

    // Update the file and preview
    currentFile.value = rotatedFile;

    // Revoke old preview URL and create new one
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value);
    }
    previewUrl.value = URL.createObjectURL(rotatedFile);

    // Emit events with the rotated file
    emit("update:modelValue", rotatedFile);
    emit("change", rotatedFile);
  } catch (error) {
    console.error("Failed to rotate image:", error);
  } finally {
    isRotating.value = false;
  }
}

function getDisplayUrl(): string {
  if (previewUrl.value) return previewUrl.value;
  if (props.currentImageUrl) return props.currentImageUrl;
  return "";
}

const sizeClasses = computed(() => {
  if (props.large) {
    return props.square ? "h-40 w-full" : "h-40 w-40";
  }
  return "h-20 w-20";
});

const shapeClasses = computed(() => {
  return props.square ? "rounded-lg" : "rounded-full";
});

const containerClasses = computed(() => {
  const base =
    "group relative cursor-pointer overflow-hidden border-2 border-black-dark bg-gray-100 dark:bg-kekkle-dark-light";
  return `${base} ${sizeClasses.value} ${shapeClasses.value}`;
});
</script>

<template>
  <div class="shrink-0" :class="large && square ? 'w-full' : ''">
    <input-label :text="label" :disabled="labelDisabled" />
    <div :class="containerClasses" @click="triggerFileInput">
      <img
        v-if="getDisplayUrl()"
        :src="getDisplayUrl()"
        alt="Profile"
        class="h-full w-full object-cover"
      />
      <div
        v-else
        class="flex h-full w-full items-center justify-center text-2xl"
      >
        🏞️ 📤
      </div>
      <!-- Hover overlay -->
      <div
        class="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <span class="text-xs font-bold text-white">
          {{ t("settings.change") }}
        </span>
      </div>
    </div>

    <!-- Rotate button -->
    <button
      v-if="showRotateButton && currentFile"
      type="button"
      :disabled="isRotating"
      class="mt-2 flex items-center gap-2 rounded-sm border-2 border-kekkle-dark-dark px-3 py-2 text-sm font-medium text-kekkle-dark shadow-blue-hard-sm transition-opacity hover:opacity-80 disabled:opacity-50 dark:text-slate-200"
      :class="isRotating ? 'animate-pulse' : ''"
      @click.stop="rotateImage"
    >
      <refresh-icon class="h-4 w-4" />
      {{
        isRotating
          ? t("global.inputs.labels.rotating")
          : t("global.inputs.labels.rotate")
      }}
    </button>

    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
